import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FaCircle,
  FaDownload,
  FaFont,
  FaImage,
  FaMinus,
  FaPlus,
  FaRedo,
  FaSave,
  FaSlash,
  FaSquare,
  FaTrash,
  FaUndo,
} from "react-icons/fa";

import api from "../../api/axios";
import Inspector from "./Inspector";
import CanvasSurface from "./CanvasSurface";
import {
  BACKGROUNDS,
  CANVAS_SIZES,
  DEFAULT_H,
  DEFAULT_W,
  MAX_IMAGE_EDGE,
  createElement,
} from "./constants";
import { exportPng, fileToScaledDataUrl } from "./exportPng";

const AUTOSAVE_MS = 900;
const MAX_HISTORY = 60;

const emptyDesign = () => ({
  name: "Untitled design",
  width: DEFAULT_W,
  height: DEFAULT_H,
  background: { ...BACKGROUNDS[0] },
});

function ToolButton({ title, onClick, disabled, children, active }) {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      style={{
        border: "1px solid rgba(148,163,184,0.35)",
        borderRadius: "9px",
        padding: "7px 10px",
        background: active ? "#ff6b35" : "rgba(255,255,255,0.06)",
        color: active ? "#fff" : "inherit",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "0.82rem",
      }}
    >
      {children}
    </button>
  );
}

const CanvasStudio = forwardRef(function CanvasStudio(
  { notes, editable, dark, onNoteInput, onNoteSave },
  ref
) {
  const [designId, setDesignId] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [design, setDesign] = useState(emptyDesign);
  const [elements, setElements] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [zoom, setZoom] = useState(null); // null = fit to pane
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasUnsaved, setHasUnsaved] = useState(false);

  const viewportRef = useRef(null);
  const fileRef = useRef(null);
  const saveTimer = useRef(null);
  const dirty = useRef(false);
  const inFlight = useRef(false);
  const history = useRef({ past: [], future: [] });
  const [historyTick, setHistoryTick] = useState(0);

  const notesById = useMemo(
    () =>
      notes.reduce((acc, n) => {
        acc[n._id] = { text: n.text, paletteIndex: n.color };
        return acc;
      }, {}),
    [notes]
  );

  const selected = elements.find((e) => e.id === selectedId) || null;

  // `dirty` is a ref because save paths read it mid-flight, but the Save
  // button and the unload guard need to re-render on it — so mirror it.
  const setDirty = useCallback((value) => {
    dirty.current = value;
    setHasUnsaved(value);
  }, []);

  // ---- Zoom: fit the design into the viewport unless the user overrode it ----
  const [fitScale, setFitScale] = useState(0.5);
  useLayoutEffect(() => {
    const measure = () => {
      const box = viewportRef.current;
      if (!box) return;
      const pad = 48;
      setFitScale(
        Math.min(
          1,
          (box.clientWidth - pad) / design.width,
          (box.clientHeight - pad) / design.height
        )
      );
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [design.width, design.height]);

  const scale = zoom ?? Math.max(0.1, fitScale);

  // ---- Load ----
  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const { data: list } = await api.get("/canvases");
        if (!active) return;
        setDesigns(list);

        if (list.length === 0) {
          setLoading(false);
          return;
        }

        const { data: full } = await api.get(`/canvases/${list[0]._id}`);
        if (!active) return;

        setDesignId(full._id);
        setDesign({
          name: full.name,
          width: full.width,
          height: full.height,
          background: full.background,
        });
        setElements(full.elements || []);
      } catch {
        if (active) setError("Could not load designs. Is the server running?");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  // ---- Save (debounced autosave) ----
  const save = useCallback(async () => {
    if (!editable) return;
    // An in-flight create must finish before anything else saves, or a
    // second autosave would POST a duplicate design instead of updating it.
    if (inFlight.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(save, 250);
      return;
    }

    const body = { ...design, elements };

    inFlight.current = true;
    // Cleared up front, so an edit made mid-request re-marks the design
    // dirty rather than being swallowed by this save's success.
    setDirty(false);

    try {
      setStatus("Saving…");
      if (designId) {
        await api.put(`/canvases/${designId}`, body);
      } else {
        const { data } = await api.post("/canvases", body);
        setDesignId(data._id);
        setDesigns((d) => [data, ...d]);
      }
      setStatus(dirty.current ? "Unsaved changes" : "All changes saved");
    } catch {
      setDirty(true);
      setStatus("");
      setError("Could not save the design.");
    } finally {
      inFlight.current = false;
      if (dirty.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(save, AUTOSAVE_MS);
      }
    }
  }, [design, designId, editable, elements, setDirty]);

  useEffect(() => {
    if (!dirty.current || !editable) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(save, AUTOSAVE_MS);
    return () => clearTimeout(saveTimer.current);
  }, [design, elements, editable, save]);

  const markDirty = useCallback(() => {
    setDirty(true);
    setStatus("Unsaved changes");
  }, [setDirty]);

  // ---- History ----
  // Callers snapshot *before* they mutate, so one drag pushes a single entry
  // (at gesture start) instead of one per pointermove.
  const pushHistory = useCallback(() => {
    history.current.past.push({ design, elements });
    if (history.current.past.length > MAX_HISTORY) history.current.past.shift();
    history.current.future = [];
    setHistoryTick((t) => t + 1);
  }, [design, elements]);

  const undo = useCallback(() => {
    const prev = history.current.past.pop();
    if (!prev) return;
    history.current.future.push({ design, elements });
    setDesign(prev.design);
    setElements(prev.elements);
    setSelectedId(null);
    setEditingId(null);
    markDirty();
    setHistoryTick((t) => t + 1);
  }, [design, elements, markDirty]);

  const redo = useCallback(() => {
    const next = history.current.future.pop();
    if (!next) return;
    history.current.past.push({ design, elements });
    setDesign(next.design);
    setElements(next.elements);
    setSelectedId(null);
    setEditingId(null);
    markDirty();
    setHistoryTick((t) => t + 1);
  }, [design, elements, markDirty]);

  // ---- Element mutations ----
  const patchElement = useCallback(
    (id, patch) => {
      setElements((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
      markDirty();
    },
    [markDirty]
  );

  const topZ = () => elements.reduce((m, e) => Math.max(m, e.z || 0), 0);

  const addElement = (type, overrides = {}) => {
    const el = createElement(type, {
      z: topZ() + 1,
      // Stagger new elements so a run of them doesn't stack invisibly.
      x: Math.round(60 + (elements.length % 5) * 24),
      y: Math.round(60 + (elements.length % 5) * 24),
      ...overrides,
    });
    pushHistory();
    setElements((prev) => [...prev, el]);
    setSelectedId(el.id);
    markDirty();
    if (type === "text") setEditingId(el.id);
  };

  const deleteElement = useCallback(
    (id) => {
      pushHistory();
      setElements((prev) => prev.filter((e) => e.id !== id));
      setSelectedId((s) => (s === id ? null : s));
      setEditingId((s) => (s === id ? null : s));
      markDirty();
    },
    [pushHistory, markDirty]
  );

  const changeLayer = (id, dir) => {
    pushHistory();
    markDirty();
    setElements((prev) => {
      const sorted = [...prev].sort((a, b) => (a.z || 0) - (b.z || 0));
      const i = sorted.findIndex((e) => e.id === id);
      if (i === -1) return prev;

      const j =
        dir === "front" ? sorted.length - 1
        : dir === "back" ? 0
        : dir === "up" ? Math.min(sorted.length - 1, i + 1)
        : Math.max(0, i - 1);

      const [moved] = sorted.splice(i, 1);
      sorted.splice(j, 0, moved);
      // Re-index so z stays dense and comparisons stay stable.
      const zById = new Map(sorted.map((e, idx) => [e.id, idx]));
      return prev.map((e) => ({ ...e, z: zById.get(e.id) ?? e.z }));
    });
  };

  const dropNote = (noteId, x, y) => {
    const note = notes.find((n) => n._id === noteId);
    if (!note) return;
    addElement("note", {
      noteId,
      x,
      y,
      text: note.text,
      paletteIndex: note.color,
    });
  };

  // Clicking a card in the library places a note without a drag — the only
  // route that works on touch, where HTML5 drag events never fire.
  useImperativeHandle(ref, () => ({
    placeNote: (noteId) => {
      const offset = (elements.length % 6) * 26;
      dropNote(
        noteId,
        Math.round(design.width / 2 - 105 + offset),
        Math.round(design.height / 2 - 100 + offset)
      );
    },
  }));

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;

    try {
      const { src, width, height } = await fileToScaledDataUrl(file, MAX_IMAGE_EDGE);
      // Fit the image inside the canvas while keeping its aspect ratio.
      const ratio = Math.min(1, (design.width * 0.6) / width, (design.height * 0.6) / height);
      addElement("image", {
        src,
        w: Math.round(width * ratio),
        h: Math.round(height * ratio),
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const setBackground = (bg) => {
    pushHistory();
    setDesign((d) => ({ ...d, background: { ...bg } }));
    markDirty();
  };

  const setSize = (w, h) => {
    pushHistory();
    setDesign((d) => ({ ...d, width: w, height: h }));
    markDirty();
  };

  // Switching designs throws away anything the debounce hasn't written yet,
  // so make that an explicit choice rather than a silent loss.
  const confirmDiscard = () =>
    !dirty.current ||
    window.confirm("This design has unsaved changes. Discard them?");

  const newDesign = () => {
    clearTimeout(saveTimer.current);
    history.current = { past: [], future: [] };
    setDesignId(null);
    setDesign(emptyDesign());
    setElements([]);
    setSelectedId(null);
    setDirty(true);
    setStatus("Unsaved changes");
  };

  const openDesign = async (id) => {
    if (!confirmDiscard()) return;
    if (!id) return newDesign();
    clearTimeout(saveTimer.current);
    try {
      const { data } = await api.get(`/canvases/${id}`);
      history.current = { past: [], future: [] };
      setDesignId(data._id);
      setDesign({
        name: data.name,
        width: data.width,
        height: data.height,
        background: data.background,
      });
      setElements(data.elements || []);
      setSelectedId(null);
      setDirty(false);
      setStatus("");
    } catch {
      setError("Could not open that design.");
    }
  };

  const removeDesign = async () => {
    if (!designId) return newDesign();
    if (!window.confirm(`Delete "${design.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/canvases/${designId}`);
      setDesigns((d) => d.filter((x) => x._id !== designId));
      newDesign();
      setDirty(false);
      setStatus("");
    } catch {
      setError("Could not delete that design.");
    }
  };

  const doExport = async () => {
    try {
      setStatus("Rendering PNG…");
      await exportPng(
        { ...design, elements },
        {
          resolveNote: (el) => notesById[el.noteId] || null,
          fileName: `${(design.name || "design").replace(/[^\w-]+/g, "-")}.png`,
        }
      );
      setStatus(dirty.current ? "Unsaved changes" : "All changes saved");
    } catch (err) {
      setError(err.message);
    }
  };

  // ---- Keyboard shortcuts ----
  useEffect(() => {
    if (!editable) return undefined;

    const onKey = (e) => {
      // Ctrl+S is checked before the field guard — it should work while
      // you're mid-sentence in a text element, which is exactly when the
      // urge to save hits.
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        clearTimeout(saveTimer.current);
        return save();
      }

      // Never hijack ordinary typing in a field.
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        return e.shiftKey ? redo() : undo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") {
        e.preventDefault();
        return redo();
      }
      if (!selectedId) return;

      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        return deleteElement(selectedId);
      }

      const nudge = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[
        e.key
      ];
      if (nudge) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const el = elements.find((x) => x.id === selectedId);
        if (el) patchElement(selectedId, { x: el.x + nudge[0] * step, y: el.y + nudge[1] * step });
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [editable, selectedId, elements, undo, redo, deleteElement, patchElement, save]);

  // ---- Unload guard ----
  // Only registered while there's something to lose; a permanently-attached
  // beforeunload handler disqualifies the page from the back/forward cache.
  useEffect(() => {
    if (!hasUnsaved || !editable) return undefined;

    const onBeforeUnload = (e) => {
      e.preventDefault();
      // Browsers ignore custom text now, but returnValue is still what
      // actually triggers the prompt in Chrome.
      e.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsaved, editable]);

  const panelBg = dark ? "#111c30" : "#ffffff";
  const panelText = dark ? "#e2e8f0" : "#334155";
  const border = dark ? "1px solid #1f2f49" : "1px solid #e2e8f0";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: panelBg,
        color: panelText,
        borderRadius: "14px",
        border,
        overflow: "hidden",
      }}
    >
      {/* Toolbar */}
      <div
        className="d-flex flex-wrap align-items-center gap-2"
        style={{ padding: "10px 12px", borderBottom: border }}
      >
        {editable && (
          <>
            <ToolButton title="Add text" onClick={() => addElement("text")}>
              <FaFont /> Text
            </ToolButton>
            <ToolButton title="Add rectangle" onClick={() => addElement("rect")}>
              <FaSquare />
            </ToolButton>
            <ToolButton title="Add ellipse" onClick={() => addElement("ellipse")}>
              <FaCircle />
            </ToolButton>
            <ToolButton title="Add line" onClick={() => addElement("line")}>
              <FaSlash />
            </ToolButton>
            <ToolButton title="Add image" onClick={() => fileRef.current?.click()}>
              <FaImage />
            </ToolButton>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={pickImage}
              style={{ display: "none" }}
            />

            <span style={{ width: "1px", height: "24px", background: "rgba(148,163,184,0.35)" }} />

            <ToolButton
              title="Undo (Ctrl+Z)"
              onClick={undo}
              disabled={history.current.past.length === 0}
            >
              <FaUndo />
            </ToolButton>
            <ToolButton
              title="Redo (Ctrl+Shift+Z)"
              onClick={redo}
              disabled={history.current.future.length === 0}
            >
              <FaRedo />
            </ToolButton>
          </>
        )}

        <ToolButton title="Zoom out" onClick={() => setZoom(Math.max(0.1, scale - 0.1))}>
          <FaMinus />
        </ToolButton>
        <button
          onClick={() => setZoom(null)}
          title="Fit to pane"
          style={{
            border: "none",
            background: "none",
            color: "inherit",
            cursor: "pointer",
            fontSize: "0.8rem",
            minWidth: "44px",
          }}
        >
          {Math.round(scale * 100)}%
        </button>
        <ToolButton title="Zoom in" onClick={() => setZoom(Math.min(3, scale + 0.1))}>
          <FaPlus />
        </ToolButton>

        <div className="ms-auto d-flex align-items-center gap-2">
          <span
            style={{
              fontSize: "0.72rem",
              color: hasUnsaved ? "#f59e0b" : "#94a3b8",
              whiteSpace: "nowrap",
            }}
          >
            {status}
          </span>

          {editable && (
            <button
              onClick={() => {
                clearTimeout(saveTimer.current);
                save();
              }}
              disabled={!hasUnsaved}
              title={hasUnsaved ? "Save now (Ctrl+S)" : "Everything is saved"}
              className="fw-bold"
              style={{
                border: "none",
                borderRadius: "9px",
                padding: "7px 14px",
                background: hasUnsaved ? "#ff6b35" : "rgba(148,163,184,0.25)",
                color: hasUnsaved ? "#fff" : "#94a3b8",
                cursor: hasUnsaved ? "pointer" : "default",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "0.82rem",
              }}
            >
              <FaSave /> Save
            </button>
          )}

          <ToolButton title="Export as PNG" onClick={doExport}>
            <FaDownload /> PNG
          </ToolButton>
        </div>
      </div>

      {/* Design + background + size controls */}
      {editable && (
        <div
          className="d-flex flex-wrap align-items-center gap-2"
          style={{ padding: "8px 12px", borderBottom: border }}
        >
          <input
            value={design.name}
            onChange={(e) => {
              setDesign((d) => ({ ...d, name: e.target.value }));
              markDirty();
            }}
            onBlur={save}
            placeholder="Design name"
            style={{
              border: "1px solid rgba(148,163,184,0.35)",
              borderRadius: "9px",
              padding: "6px 10px",
              background: "rgba(255,255,255,0.06)",
              color: "inherit",
              outline: "none",
              flex: "1 1 150px",
              minWidth: "120px",
              fontSize: "0.82rem",
            }}
          />

          <select
            value={designId || ""}
            onChange={(e) => openDesign(e.target.value)}
            title="Open a saved design"
            style={{
              border: "1px solid rgba(148,163,184,0.35)",
              borderRadius: "9px",
              padding: "6px 8px",
              background: "rgba(255,255,255,0.06)",
              color: "inherit",
              fontSize: "0.8rem",
            }}
          >
            <option value="" style={{ color: "#0f172a" }}>
              + New design
            </option>
            {designs.map((d) => (
              <option key={d._id} value={d._id} style={{ color: "#0f172a" }}>
                {d.name}
              </option>
            ))}
          </select>

          <select
            value={design.background.kind}
            onChange={(e) =>
              setBackground(BACKGROUNDS.find((b) => b.kind === e.target.value) || BACKGROUNDS[0])
            }
            title="Canvas background"
            style={{
              border: "1px solid rgba(148,163,184,0.35)",
              borderRadius: "9px",
              padding: "6px 8px",
              background: "rgba(255,255,255,0.06)",
              color: "inherit",
              fontSize: "0.8rem",
            }}
          >
            {BACKGROUNDS.map((b) => (
              <option key={b.kind} value={b.kind} style={{ color: "#0f172a" }}>
                {b.label}
              </option>
            ))}
          </select>

          <select
            value={`${design.width}x${design.height}`}
            onChange={(e) => {
              const [w, h] = e.target.value.split("x").map(Number);
              setSize(w, h);
            }}
            title="Canvas size"
            style={{
              border: "1px solid rgba(148,163,184,0.35)",
              borderRadius: "9px",
              padding: "6px 8px",
              background: "rgba(255,255,255,0.06)",
              color: "inherit",
              fontSize: "0.8rem",
            }}
          >
            {CANVAS_SIZES.map((s) => (
              <option
                key={s.label}
                value={`${s.width}x${s.height}`}
                style={{ color: "#0f172a" }}
              >
                {s.label}
              </option>
            ))}
          </select>

          <ToolButton title="Delete this design" onClick={removeDesign}>
            <FaTrash />
          </ToolButton>
        </div>
      )}

      {error && (
        <div
          style={{
            background: "#fee2e2",
            color: "#b91c1c",
            padding: "8px 12px",
            fontSize: "0.8rem",
          }}
        >
          {error}
        </div>
      )}

      {/* Canvas viewport + inspector */}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <div
          ref={viewportRef}
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "auto",
            padding: "24px",
            background: dark ? "#0b1120" : "#f1f5f9",
          }}
        >
          {loading ? (
            <p style={{ color: "#94a3b8", fontStyle: "italic" }}>Loading design…</p>
          ) : (
            <div
              style={{
                width: `${design.width * scale}px`,
                height: `${design.height * scale}px`,
                margin: "0 auto",
              }}
            >
              <CanvasSurface
                design={design}
                elements={elements}
                notesById={notesById}
                selectedId={selectedId}
                editingId={editingId}
                editable={editable}
                scale={scale}
                onSelect={setSelectedId}
                onStartEdit={setEditingId}
                onEndEdit={() => setEditingId(null)}
                onPatch={patchElement}
                onBeforeChange={pushHistory}
                onDropNote={dropNote}
                onNoteInput={onNoteInput}
                onNoteSave={onNoteSave}
              />
            </div>
          )}
        </div>

        {editable && (
          <aside
            style={{
              width: "240px",
              flexShrink: 0,
              borderLeft: border,
              overflowY: "auto",
            }}
          >
            <Inspector
              key={selectedId}
              el={selected}
              onPatch={patchElement}
              onBeforeChange={pushHistory}
              onDelete={deleteElement}
              onLayer={changeLayer}
            />
          </aside>
        )}
      </div>

      {/* historyTick keeps the undo/redo buttons in sync with the ref-held stacks */}
      <span hidden>{historyTick}</span>
    </div>
  );
});

export default CanvasStudio;
